// App-wide state: store settings, customer account, shopping cart (localStorage) and toasts.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '../components/Icon';
import { faCircleCheck, faCircleExclamation, faTriangleExclamation, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { api } from './api';
import { money as fmtMoney, storageGet, storageSet } from './format';
import { pickField, translate } from './i18n';

const SettingsCtx = createContext(null);
const AccountCtx = createContext(null);
const DialogCtx = createContext(null);
const CartCtx = createContext(null);
const ToastCtx = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api('/api/settings').then(setSettings).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const value = useMemo(() => ({
    settings,
    error,
    reload: load,
    money: (n) => fmtMoney(n, settings?.currency || 'AED'),
  }), [settings, error, load]);
  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

// Logged-in customer: undefined while checking, null when logged out.
export function AccountProvider({ children }) {
  const [user, setUser] = useState(undefined);
  useEffect(() => { api('/api/account/session').then(setUser).catch(() => setUser(null)); }, []);
  const value = useMemo(() => ({
    user,
    setUser,
    logout: async () => { await api('/api/account/logout', { method: 'POST' }).catch(() => {}); setUser(null); },
  }), [user]);
  return <AccountCtx.Provider value={value}>{children}</AccountCtx.Provider>;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => storageGet('tev_cart', []));
  const [coupon, setCouponState] = useState(() => storageGet('tev_coupon', ''));
  useEffect(() => storageSet('tev_cart', items), [items]);
  useEffect(() => storageSet('tev_coupon', coupon), [coupon]);

  const value = useMemo(() => ({
    items,
    coupon,
    count: items.reduce((a, i) => a + i.qty, 0),
    add: (id, qty = 1) => setItems((cur) => {
      const hit = cur.find((i) => i.id === id);
      return hit ? cur.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i)) : [...cur, { id, qty }];
    }),
    setQty: (id, qty) => setItems((cur) => cur.map((i) => (i.id === id ? { ...i, qty } : i)).filter((i) => i.qty > 0)),
    remove: (id) => setItems((cur) => cur.filter((i) => i.id !== id)),
    keepOnly: (ids) => setItems((cur) => cur.filter((i) => ids.has(i.id))),
    clear: () => setItems([]),
    setCoupon: (c) => setCouponState(String(c || '').trim().toUpperCase()),
  }), [items, coupon]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timer = useRef();
  const show = useCallback((message, opts = {}) => {
    clearTimeout(timer.current);
    setToast({ message, key: Date.now(), ...opts });
    timer.current = setTimeout(() => setToast(null), opts.duration || 3200);
  }, []);
  const value = useMemo(() => ({ show, error: (m) => show(m, { type: 'error' }) }), [show]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      {toast && (
        <div key={toast.key} role="status"
          className={`fade-up fixed inset-x-0 bottom-6 z-[100] mx-auto flex w-fit max-w-[calc(100vw-2rem)] items-center gap-3 rounded-xl px-4 py-3 text-sm text-white shadow-card ${toast.type === 'error' ? 'bg-sale' : 'bg-navy'}`}>
          <FontAwesomeIcon icon={toast.type === 'error' ? faCircleExclamation : faCircleCheck} className={toast.type === 'error' ? '' : 'text-accent'} />
          <span>{toast.message}</span>
          {toast.action && <Link to={toast.action.to} className="font-bold whitespace-nowrap text-accent hover:underline">{toast.action.label}</Link>}
        </div>
      )}
    </ToastCtx.Provider>
  );
}

// In-site replacement for window.alert / window.confirm.
//   await dialog.alert('Message', { title, tone: 'error' })
//   if (await dialog.confirm('Delete it?', { confirmLabel: 'Delete', danger: true })) …
function DialogBox({ d, onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    el.showModal();
    return () => el.close();
  }, []);
  const isConfirm = d.kind === 'confirm';
  const tone = d.tone || (isConfirm ? (d.danger ? 'danger' : 'info') : 'info');
  const icon = { error: faCircleExclamation, danger: faTriangleExclamation, info: isConfirm ? faCircleQuestion : faCircleCheck }[tone];
  const iconCls = { error: 'bg-red-50 text-sale', danger: 'bg-red-50 text-sale', info: 'bg-brand-50 text-brand-600' }[tone];
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); onDone(false); }}
      onClick={(e) => { if (e.target === ref.current) onDone(false); }}
      className="modal-pop m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white p-0 text-ink shadow-lift backdrop:bg-navy/50 backdrop:backdrop-blur-[2px]">
      <div className="flex gap-4 p-6">
        <span className={`grid size-11 shrink-0 place-items-center rounded-full text-xl ${iconCls}`}><FontAwesomeIcon icon={icon} /></span>
        <div className="min-w-0 pt-1">
          <h2 className="text-lg font-bold">{d.title || translate(isConfirm ? 'Please confirm' : tone === 'error' ? 'Not allowed' : 'Notice')}</h2>
          <p className="mt-1.5 text-[15px] whitespace-pre-line text-slate-600">{d.message}</p>
        </div>
      </div>
      <footer className="flex justify-end gap-2.5 border-t border-slate-200 bg-slate-50 px-6 py-3.5">
        {isConfirm && <button className="btn btn-outline btn-sm" onClick={() => onDone(false)}>{d.cancelLabel || translate('Cancel')}</button>}
        <button autoFocus className={`btn btn-sm ${d.danger ? 'bg-sale text-white hover:bg-red-700' : 'btn-primary'}`} onClick={() => onDone(true)}>
          {d.confirmLabel || translate(isConfirm ? 'Yes, continue' : 'OK')}
        </button>
      </footer>
    </dialog>
  );
}

export function DialogProvider({ children }) {
  const [d, setD] = useState(null);
  const open = useCallback((kind, message, opts = {}) => new Promise((resolve) => setD({ kind, message, ...opts, resolve, key: Date.now() })), []);
  const value = useMemo(() => ({
    alert: (message, opts) => open('alert', message, opts),
    confirm: (message, opts) => open('confirm', message, opts),
  }), [open]);
  const done = (result) => { d.resolve(result); setD(null); };
  return (
    <DialogCtx.Provider value={value}>
      {children}
      {d && <DialogBox key={d.key} d={d} onDone={done} />}
    </DialogCtx.Provider>
  );
}

export const useDialog = () => useContext(DialogCtx);
export const useSettings = () => useContext(SettingsCtx);
export const useAccount = () => useContext(AccountCtx);
export const useCart = () => useContext(CartCtx);
export const useToast = () => useContext(ToastCtx);

// Adds to cart and shows a toast with a "View cart" link.
export function useAddToCart() {
  const cart = useCart();
  const toast = useToast();
  return (product, qty = 1) => {
    cart.add(product.id, qty);
    toast.show(translate('{name} added to cart', { name: pickField(product, 'name') }), { action: { label: translate('View cart'), to: '/cart' } });
  };
}

// Server-side priced cart quote, refreshed whenever cart or coupon changes.
export function useQuote() {
  const { items, coupon } = useCart();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api('/api/cart/quote', { method: 'POST', body: { items, coupon } })
      .then((q) => { if (alive) setQuote(q); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [items, coupon]);
  return { quote, loading };
}
